// Model Lab payload types and helpers.

export interface LabEvent {
  type: string;
  data: Record<string, unknown>;
}

export interface LabSource {
  source_type?: string | null;
  source_id?: string | null;
  chunk_id?: string | null;
  node_id?: string | null;
  title?: string | null;
  excerpt?: string | null;
  why_selected?: string | null;
  confidence?: number | null;
  freshness?: string | null;
  review_status?: string | null;
  user_facing_eligible?: boolean | null;
  source_url?: string | null;
  provenance?: { url?: string | null; source_title?: string | null } | null;
}

export interface LabSpan {
  span_id?: string | null;
  parent_span_id?: string | null;
  trace_id?: string | null;
  stage?: string | null;
  status?: string | null;
  latency_ms?: number | null;
}

export interface LabFeedback {
  feedback_status?: string;
  note?: string;
  eval_case_id?: string | null;
}

export interface LabRun {
  run_id?: string;
  trace_id?: string | null;
  recorded_at?: string | null;
  session_id?: string | null;
  message_preview?: string | null;
  response_preview?: string | null;
  assistant_response?: string | null;
  classification?: string | null;
  confidence?: number | null;
  latency_ms?: number | null;
  graph_hit?: boolean | null;
  knowledge_hit?: boolean | null;
  cache_hit?: boolean | null;
  guardrail_ok?: boolean | null;
  token_usage?: Record<string, number> | null;
  provider_usage?: Record<string, unknown> | null;
  retrieval?: Record<string, unknown> | null;
  guardrail_details?: Record<string, unknown> | null;
  quality_flags?: Record<string, unknown> | null;
  quality?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  spans?: LabSpan[] | null;
  source_details?: LabSource[] | null;
  sources?: LabSource[] | null;
  commands?: Array<Record<string, unknown>> | null;
  stages?: Array<Record<string, unknown>> | null;
  request?: Record<string, unknown> | null;
  response?: Record<string, unknown> | null;
  feedback?: LabFeedback | null;
  feedback_status?: string | null;
  events?: LabEvent[] | null;
}

export interface LabRunsResponse {
  runs: LabRun[];
}

// ---------------------------------------------------------- lab helpers

export function rec(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function str(value: unknown): string {
  return typeof value === "string" ? value : value === null || value === undefined ? "" : String(value);
}

export function pretty(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return String(value);
  }
}

export function isHashLike(s: string): boolean {
  return /^[0-9a-f]{32,}$/i.test(s.trim());
}

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  knowledge_chunk: "Knowledge base",
  graph_path: "Graph path",
  risk_signal: "Risk signal",
  url: "Web indicator",
  domain: "Web indicator",
  entity: "Entity",
};

export function sourceTypeLabel(t: string | null | undefined): string {
  const key = str(t);
  return (
    SOURCE_TYPE_LABELS[key] ??
    (key || "Source").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/** Hashes are not labels. Fall back to the excerpt, then to the type name. */
export function sourceLabel(s: LabSource): string {
  const title = str(s.title).trim();
  if (title && !isHashLike(title)) return title.replace(/_/g, " ");
  const excerpt = str(s.excerpt).trim();
  if (excerpt) return excerpt.length > 64 ? `${excerpt.slice(0, 63)}…` : excerpt;
  return sourceTypeLabel(s.source_type);
}

export function sourceBody(s: LabSource): string {
  return str(s.excerpt || s.why_selected).trim();
}

export function sourceRef(s: LabSource): string {
  const id = str(s.chunk_id || s.node_id || s.source_id).trim();
  return id && isHashLike(id) ? `${id.slice(0, 10)}…` : id;
}

// Detection is ALWAYS the Knowledge Graph. This selector only picks the REPLY
// engine, and it is branded — the client never sees the vendor/model string.
export const REPLY_ENGINES: Record<string, { label: string; provider: string; model: string; hint: string }> = {
  gemma: {
    label: "Gemma",
    provider: "google",
    model: "gemini-2.5-flash",
    hint: "AI assistant writes a grounded reply",
  },
  offline: {
    label: "Offline (no AI)",
    provider: "rule",
    model: "langgraph-rules-v1",
    hint: "Template reply, no external AI",
  },
};

export function brandReplyEngine(provider: string): string {
  if (provider === "google") return "Gemma";
  if (provider === "rule") return "Offline (no AI)";
  return provider || "Gemma";
}

export const SAMPLES = [
  "Someone asked me to scan a QR to receive refund money",
  "I got a KYC verification link asking for OTP",
  "Please check https://hdfc-verify-login.com before I click",
  "How do UPI refund scams work?",
];

export const FEEDBACK_STATUSES = ["good", "bad", "needs_review", "promote_to_eval"] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];
